import React, { useCallback, useEffect, useRef, useState } from "react";
import {
	ReactFlow,
	useNodesState,
	useEdgesState,
	MiniMap,
	Controls,
	Background,
} from "@xyflow/react";
import OrgNode from "./OrgNode";
import { compactCurrency } from "./costFormat";
import { flowRemountKey } from "./viewportKey";
import dagre from "dagre";
import "@xyflow/react/dist/style.css";

// Cards size to their content rather than a single fixed width, clamped to this range: a min so a
// short label ("Support") still reads as a card, a max so a very long name wraps/ellipsizes instead
// of stretching the whole row.
const MIN_NODE_WIDTH = 168;
const MAX_NODE_WIDTH = 420;
const nodeWidth = MIN_NODE_WIDTH; // fallback for drag/center math when a node lacks a measured width
// Must match the rendered card heights in OrgNode. The root card is the taller "trunk"; children are
// shorter. Dagre positions nodes assuming these box heights; a mismatch leaves phantom vertical gaps
// and makes the connector elbows look cropped/uneven, so keep these in sync with OrgNode.
const nodeHeight = 92;
const rootNodeHeight = 100;
// Parent cards are taller to hold the in-card "Show N areas" fold footer; leaves stay compact.
const FOOTER_HEIGHT = 32;
const heightForNode = (node) =>
	node.id === "root"
		? rootNodeHeight
		: (node.data?.hasChildren ? nodeHeight + FOOTER_HEIGHT : nodeHeight);
// Horizontal chrome around the label: px-4 (32) on the card + px-1 (8) on the label + safety.
const NODE_H_PADDING = 44;

// Measure text width with a real canvas context (synchronous and accurate, unlike a per-char guess
// that under-sizes wide faces and truncates labels). Cached context; falls back to a char estimate
// when canvas measurement is unavailable (e.g. jsdom in tests).
let _measureCtx = null;
const measureText = (text, font, fallbackPerChar) => {
	const s = String(text ?? "");
	if (typeof document !== "undefined") {
		if (!_measureCtx) {
			const canvas = document.createElement("canvas");
			_measureCtx = canvas.getContext ? canvas.getContext("2d") : null;
		}
		if (_measureCtx) {
			_measureCtx.font = font;
			const w = _measureCtx.measureText(s).width;
			if (w) return w;
		}
	}
	return s.length * fallbackPerChar;
};

// Size a node to the wider of its label (16px semibold) and its secondary "% of parent · €x direct"
// line (12px). Done synchronously so dagre and the rendered card agree on one number without waiting
// on async DOM measurement (incomplete measurement on large trees used to leave edges undrawn).
export const estimateNodeWidth = (node) => {
	const data = node.data || {};
	const label = String(data.label ?? "");
	const isRoot = node.id === "root";
	const hasIcon = isRoot || data.system;
	const labelFont = `600 16px 'Plus Jakarta Sans', sans-serif`;
	const labelPx = measureText(label, labelFont, 8.8) + (hasIcon ? 26 : 0);
	let secondaryPx = 0;
	if (!isRoot && data.pctOfParent != null) {
		const parts = [];
		if (data.pctOfParent != null) parts.push(`${data.pctOfParent}% of parent`);
		if (data.subtree !== data.direct) parts.push(`${compactCurrency(data.direct)} direct`);
		secondaryPx = measureText(parts.join(" · "), "400 12px 'Plus Jakarta Sans', sans-serif", 6.2);
	}
	const contentPx = Math.max(labelPx, secondaryPx);
	return Math.round(Math.min(MAX_NODE_WIDTH, Math.max(MIN_NODE_WIDTH, contentPx + NODE_H_PADDING)));
};

const getLayoutedElements = (nodes, edges, direction = "TB") => {
	const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
	const isHorizontal = direction === "LR";
	// ranksep is the vertical gap between hierarchy levels; a generous gap reads as a clean,
	// intentional org chart (the old 30px crammed the root against its children). nodesep is
	// the horizontal gap between siblings.
	dagreGraph.setGraph({ rankdir: direction, nodesep: 40, ranksep: 64 });

	const widthById = {};
	nodes.forEach((node) => {
		const height = heightForNode(node);
		const width = estimateNodeWidth(node);
		widthById[node.id] = width;
		dagreGraph.setNode(node.id, { width, height });
	});

	edges.forEach((edge) => {
		dagreGraph.setEdge(edge.source, edge.target);
	});

	dagre.layout(dagreGraph);

	// Align same-rank nodes by their TOP edge. dagre centres them, which — now that cards vary in
	// height — made connectors enter siblings at different heights and look cluttered. Every node in
	// a rank shares a dagre y-centre, so snap each card's top to the rank's tallest card.
	const rankMaxHeight = {};
	nodes.forEach((node) => {
		const key = Math.round(dagreGraph.node(node.id).y);
		rankMaxHeight[key] = Math.max(rankMaxHeight[key] || 0, heightForNode(node));
	});

	const newNodes = nodes.map((node) => {
		const nodeWithPosition = dagreGraph.node(node.id);
		const height = heightForNode(node);
		const width = widthById[node.id];
		const rankTopY = nodeWithPosition.y - (rankMaxHeight[Math.round(nodeWithPosition.y)] || height) / 2;
		const newNode = {
			...node,
			targetPosition: isHorizontal ? "left" : "top",
			sourcePosition: isHorizontal ? "right" : "bottom",
			// Give React Flow explicit dimensions so it can route the connector edges immediately,
			// without waiting on async DOM measurement. On large trees that measurement can be
			// incomplete, which left edges to unmeasured nodes undrawn (missing connector lines).
			width,
			height,
			measured: { width, height },
			// The card reads its own width + height from data so render and layout stay in lockstep.
			data: { ...node.data, width, height },
			position: {
				x: nodeWithPosition.x - width / 2,
				y: rankTopY,
			},
		};

		return newNode;
	});

	return { nodes: newNodes, edges };
};

export default function OrgChart(props) {
	const { initialNodes, initialEdges, moveNode, externalLoadingNodeId, nodesDraggable = true, dimensionId, viewMode, anchorNodeId, focusReq, onFocusNode, onSelectNode } = props;

	const nodeTypes = {
		selectorNode: OrgNode,
	};

	const proOptions = { hideAttribution: true };

	const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
		initialNodes,
		initialEdges
	);

	const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes);
	const [edges, setEdges] = useEdgesState(layoutedEdges);
	const [draggedNodeId, setDraggedNodeId] = useState(null);
	const [hoveredNodeId, setHoveredNodeId] = useState(null);
	const [loadingNodeId, setLoadingNodeId] = useState(null);
	const rfRef = useRef(null);
	const lastFocusRef = useRef(null);

	// Structure key: only node ids and edge topology. When this changes we re-layout; otherwise we only merge data (e.g. labels).
	const structureKey =
		initialNodes.map((n) => n.id).sort().join(",") +
		"|" +
		initialEdges.map((e) => `${e.source}-${e.target}`).sort().join(",");
	// Start null so the first layout effect after every (re)mount takes the full re-layout path,
	// which always sets both nodes AND edges. Otherwise the first run is treated as a data-only
	// update, which — combined with Fast Refresh/HMR re-running the component — could leave the
	// edges unset (connector lines missing) until the next structural change.
	const prevStructureKeyRef = useRef(null);

	useEffect(() => {
		const structureChanged = prevStructureKeyRef.current !== structureKey;
		prevStructureKeyRef.current = structureKey;

		if (structureChanged) {
			const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
				initialNodes,
				initialEdges
			);
			// Keep the toggled node stationary on screen: dagre recompacts on fold/unfold, which
			// would otherwise make the clicked node jump. Offset the viewport by the node's
			// position delta so it stays under the cursor while the rest of the tree reflows.
			if (anchorNodeId && rfRef.current) {
				const oldNode = rfRef.current.getNode(anchorNodeId);
				const newNode = layoutedNodes.find((n) => n.id === anchorNodeId);
				if (oldNode && newNode) {
					const vp = rfRef.current.getViewport();
					const dx = (newNode.position.x - oldNode.position.x) * vp.zoom;
					const dy = (newNode.position.y - oldNode.position.y) * vp.zoom;
					rfRef.current.setViewport({ x: vp.x - dx, y: vp.y - dy, zoom: vp.zoom });
				}
			}
			setNodes(layoutedNodes);
			setEdges(layoutedEdges);
			setLoadingNodeId(null);
		} else {
			// Same structure (a collapse/focus toggle or a data refresh): keep every node's position
			// and only update its data + hidden flag. Collapsing therefore never re-runs the layout,
			// so siblings stay put — the collapsed node's descendants simply hide in place.
			setNodes((nds) =>
				nds.map((node) => {
					const from = initialNodes.find((n) => n.id === node.id);
					const merged = from ? { ...node, hidden: from.hidden, data: { ...node.data, ...from.data } } : node;
					// Re-apply loading/hover so they are not lost when parent re-renders with new initialNodes ref
					return {
						...merged,
						data: {
							...merged.data,
							isHovered: draggedNodeId && draggedNodeId !== node.id && hoveredNodeId === node.id,
							isLoading: loadingNodeId === node.id || externalLoadingNodeId === node.id,
						},
					};
				})
			);
			// Edges carry no layout, so just re-sync them — picks up hidden flags on collapse/focus.
			setEdges(initialEdges);
		}
	}, [structureKey, initialNodes, initialEdges, draggedNodeId, hoveredNodeId, loadingNodeId, externalLoadingNodeId, anchorNodeId, setNodes, setEdges]);

	// Update nodes with hover and loading state (loading: move target or add-parent)
	useEffect(() => {
		setNodes((nds) =>
			nds.map((node) => ({
				...node,
				data: {
					...node.data,
					isHovered: draggedNodeId && draggedNodeId !== node.id && hoveredNodeId === node.id,
					isLoading: loadingNodeId === node.id || externalLoadingNodeId === node.id,
				},
			}))
		);
	}, [hoveredNodeId, draggedNodeId, loadingNodeId, externalLoadingNodeId, setNodes]);

	// Jump-to-node from search: when a new focus request arrives, smoothly center the chart on
	// that node. The `nodes` dep lets this retry once the node enters the layout (e.g. after a
	// collapsed ancestor is revealed); lastFocusRef ensures we only center once per request.
	useEffect(() => {
		if (!focusReq || !rfRef.current) return;
		if (lastFocusRef.current === focusReq.n) return;
		// "fit" requests reframe the whole rendered set — entering focus (only the subtree is shown)
		// or returning to the full tree. Defer past the inspector's slide and the re-layout so
		// fitView measures the settled pane and the new node set, then animates to frame them. This
		// is robust to the current zoom/pane width, unlike a hand-computed setCenter.
		if (focusReq.mode === "fit") {
			lastFocusRef.current = focusReq.n;
			const t = setTimeout(() => {
				if (rfRef.current) rfRef.current.fitView({ padding: 0.2, maxZoom: 1, duration: 400 });
			}, 240);
			return () => clearTimeout(t);
		}
		// "center" requests pan to one node within the current tree (search / arrow nav), keeping zoom.
		const node = rfRef.current.getNode(focusReq.id);
		if (!node) return; // not laid out yet; a subsequent `nodes` update will re-run this
		lastFocusRef.current = focusReq.n;
		const cx = node.position.x + (node.width || node.measured?.width || nodeWidth) / 2;
		const cy = node.position.y + (node.height || node.measured?.height || nodeHeight) / 2;
		rfRef.current.setCenter(cx, cy, { zoom: rfRef.current.getViewport().zoom, duration: 400 });
	}, [focusReq, nodes]);

	const onNodeDragStart = useCallback((event, node) => {
		setDraggedNodeId(node.id);
	}, []);

	const onNodeDrag = useCallback((event, draggedNode) => {
		const draggedNodeXStart = draggedNode.position.x;
		const draggedNodeXEnd = draggedNode.position.x + (draggedNode.measured?.width || nodeWidth);
		const draggedNodeYStart = draggedNode.position.y;
		const draggedNodeYEnd = draggedNode.position.y + (draggedNode.measured?.height || nodeHeight);

		let newHoveredNodeId = null;
		nodes.forEach((node) => {
			if (node.id !== draggedNode.id && !node.hidden) {
				const targetNodeXStart = node.position.x;
				const targetNodeXEnd = node.position.x + (node.measured?.width || nodeWidth);
				const targetNodeYStart = node.position.y;
				const targetNodeYEnd = node.position.y + (node.measured?.height || nodeHeight);

				if (
					draggedNodeXStart < targetNodeXEnd &&
					draggedNodeXEnd > targetNodeXStart &&
					draggedNodeYStart < targetNodeYEnd &&
					draggedNodeYEnd > targetNodeYStart
				) {
					newHoveredNodeId = node.id;
				}
			}
		});

		setHoveredNodeId(newHoveredNodeId);
	}, [nodes]);

	const onNodeDragStop = useCallback(
		(event, draggedNode) => {
			const draggedNodeXStart = draggedNode.position.x;
			const draggedNodeXEnd = draggedNode.position.x + (draggedNode.measured?.width || nodeWidth);
			const draggedNodeYStart = draggedNode.position.y;
			const draggedNodeYEnd = draggedNode.position.y + (draggedNode.measured?.height || nodeHeight);

			let overlapFound = false;
			for (const node of nodes) {
				if (node.id !== draggedNode.id && !node.hidden) {
					const targetNodeXStart = node.position.x;
					const targetNodeXEnd = node.position.x + (node.measured?.width || nodeWidth);
					const targetNodeYStart = node.position.y;
					const targetNodeYEnd = node.position.y + (node.measured?.height || nodeHeight);

					if (
						draggedNodeXStart < targetNodeXEnd &&
						draggedNodeXEnd > targetNodeXStart &&
						draggedNodeYStart < targetNodeYEnd &&
						draggedNodeYEnd > targetNodeYStart
					) {
						overlapFound = true;
						handleOverlap(draggedNode, node);
						break;
					}
				}
			}

			if (!overlapFound) {
				redraw();
			}

			setDraggedNodeId(null);
			setHoveredNodeId(null);
		},
		// redraw is declared below this hook, so naming it here would evaluate it in its TDZ.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[nodes]
	);

	const redraw = () => {
		const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
			initialNodes,
			initialEdges
		);

		setNodes(layoutedNodes);
		setEdges(layoutedEdges);
	};

	const handleOverlap = (draggedNode, targetNode) => {
		// Reparenting reassigns the node's cost ownership, so confirm before committing. On
		// cancel, redraw from the (unchanged) source data to snap the dragged node back.
		const childLabel = draggedNode.data?.label || "this item";
		const parentLabel = targetNode.id === "root" ? "the top level" : (targetNode.data?.label || "the target");
		if (typeof window !== "undefined" && typeof window.confirm === "function" &&
			!window.confirm(`Move "${childLabel}" under "${parentLabel}"? This reassigns its allocated cost.`)) {
			redraw();
			return;
		}
		setLoadingNodeId(targetNode.id);
		draggedNode.data.hidden = true;
		const movePromise = moveNode(draggedNode.id, targetNode.id);
		redraw();
		if (movePromise && typeof movePromise.finally === "function") {
			movePromise.finally(() => setLoadingNodeId(null));
		}
	};

	// Remount (and recenter on root) only when the user switches dimension or Active/Draft —
	// NOT on node edits. Keying on node count/ids would remount on every add/remove and snap
	// the viewport back to root mid-edit; flowRemountKey deliberately ignores the node list so
	// the user's pan/zoom is preserved across add/remove/rename/move.
	const flowKey = flowRemountKey({ dimensionId, viewMode });

	// Double-click a node to focus on its subtree (it becomes a temporary root). Skip the
	// virtual company root — focusing it is just the full tree.
	const onNodeDoubleClick = useCallback((event, node) => {
		if (node?.id && node.id !== "root" && onFocusNode) onFocusNode(node.id);
	}, [onFocusNode]);

	// Single click selects a node (drives the contextual action bar); clicking the canvas
	// background clears the selection.
	const onNodeClick = useCallback((event, node) => {
		if (node?.id && onSelectNode) onSelectNode(node.id);
	}, [onSelectNode]);
	const onPaneClick = useCallback(() => {
		if (onSelectNode) onSelectNode(null);
	}, [onSelectNode]);

	// Stash the instance and frame the whole tree once the nodes have been measured (rAF runs after
	// the first paint, so the bounding box is real). Doing it here is reliable across mounts/remounts
	// and pane sizes; the focus effect's fitView handles enter/leave focus.
	const onInit = useCallback((rfInstance) => {
		rfRef.current = rfInstance;
		// Frame the tree, then open ~1.62x tighter than a bare fit so nodes are comfortably readable
		// by default. fitView is async (returns a Promise), so we AWAIT it before reading the fitted
		// zoom — otherwise we'd read the pre-fit value. We then recentre on the content's bounding
		// box at the bumped zoom via setCenter (a documented, reliable instance method).
		requestAnimationFrame(async () => {
			try {
				await rfInstance.fitView({ padding: 0.2, maxZoom: 1 });
				const fitZoom = rfInstance.getViewport().zoom;
				const ns = rfInstance.getNodes().filter((n) => !n.hidden);
				if (!ns.length) return;
				let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
				ns.forEach((n) => {
					const w = n.measured?.width ?? n.width ?? 0;
					const h = n.measured?.height ?? n.height ?? 0;
					minX = Math.min(minX, n.position.x);
					minY = Math.min(minY, n.position.y);
					maxX = Math.max(maxX, n.position.x + w);
					maxY = Math.max(maxY, n.position.y + h);
				});
				rfInstance.setCenter((minX + maxX) / 2, (minY + maxY) / 2, { zoom: fitZoom * 1.1, duration: 0 });
			} catch (_) { /* framing is best-effort */ }
		});
	}, []);

	return (
		<ReactFlow
			key={flowKey}
			nodeTypes={nodeTypes}
			nodes={nodes}
			edges={edges}
			proOptions={proOptions}
			nodesDraggable={nodesDraggable}
			colorMode="light"
			onNodesChange={onNodesChange}
			onNodeDragStart={onNodeDragStart}
			onNodeDrag={onNodeDrag}
			onNodeDragStop={onNodeDragStop}
			onNodeDoubleClick={onNodeDoubleClick}
			onNodeClick={onNodeClick}
			onPaneClick={onPaneClick}
			onInit={onInit}
			panOnScroll={true}
			zoomOnScroll={false}
			zoomOnDoubleClick={false}
			preventScrolling={true}
			// Disable React Flow's built-in Backspace/Delete node removal: it deletes from the canvas
			// client-side only (no confirmation, no API call, and ignoring our canEdit gate), which
			// made nodes vanish without being persisted and reappear on the next refetch. Removal must
			// go through the inspector's confirm dialog → backend.
			deleteKeyCode={null}
			fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
			// Faint tint behind the white cards so they read as raised surfaces (figure/ground),
			// not floating on a flat sheet.
			style={{ backgroundColor: "#f6f8fb" }}
		>
			{/* Low-contrast dot grid: gives the canvas texture and a sense of depth so the cards
			    lift off it, without competing with the tree. */}
			<Background variant="dots" gap={22} size={1.1} color="#d4dbe6" />
			{/* Zoom in/out + fit-to-view. zoomOnScroll is off, so these give users the only way
			    to zoom, and "fit view" reframes the whole tree after panning/folding. */}
			<Controls position="bottom-left" showInteractive={false} />
			{/* Minimap only earns its space on larger trees; for a handful of nodes it's clutter
			    and overlaps the top-right status. */}
			{nodes.length > 8 && (
				<MiniMap
					position="top-right"
					pannable
					zoomable
					style={{
						marginTop: '0px',
						marginRight: '40px',
						border: '1px solid #e5e7eb',
						borderRadius: '8px',
						backgroundColor: '#f9fafb',
					}}
				/>
			)}
		</ReactFlow>
	);
}
